import { Badge, Box, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { AccountMovement, CommonQueryParams, formatCurrency } from '@gofranz/common';
import { showApiErrorNotification, usePagination } from '@gofranz/common-components';
import { DataTable } from 'mantine-datatable';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useRustyState } from '../../state';

export function AccountMovements() {
  const { getAccountMovements } = useRustyState();
  const { t } = useTranslation();

  const fetchAccountMovements = useCallback(async (params: { nextPage: number; [key: string]: any }) => {
    try {
      const { nextPage, ...otherParams } = params;
      const queryParams: CommonQueryParams = {
        offset: nextPage === 1 ? 0 : 10 * (nextPage - 1),
        limit: 10,
        ...otherParams,
      };
      const response = await getAccountMovements(queryParams);
      return {
        data: response.data || [],
        total: response.total || 0,
      };
    } catch (error) {
      console.error('Failed to load account movements:', error);
      showApiErrorNotification(error, notifications, 'Failed to Load Account Movements');
      return { data: [], total: 0 };
    }
  }, [getAccountMovements]);

  const pagination = usePagination({
    perPage: 10,
    fetchData: fetchAccountMovements,
  });

  const formatDate = (dateString: string | Date) => {
    const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  const getMovementTypeColor = (amount: number) => {
    return amount > 0 ? 'green' : 'red';
  };

  const getMovementType = (amount: number) => {
    return amount > 0 ? t('glob_billing.credit') : t('glob_billing.debit');
  };

  const parseFeatures = (features?: string | null): string => {
    if (!features) return '';

    // Parse tokens format: "tokens:request=...,model=...,input=...,output=..."
    if (features.startsWith('tokens:')) {
      const params = features.substring(7); // Remove "tokens:"
      const parts = params.split(',');
      const parsed: Record<string, string> = {};

      parts.forEach(part => {
        const [key, value] = part.split('=');
        if (key && value) {
          parsed[key] = value;
        }
      });

      if (parsed.model && parsed.input && parsed.output) {
        return `Model: ${parsed.model}, Input: ${parsed.input}, Output: ${parsed.output}`;
      }
    }

    return features;
  };

  const columns = [
    {
      accessor: 'created_at',
      title: t('glob_billing.date'),
      render: (row: AccountMovement) => formatDate(row.created_at),
    },
    {
      accessor: 'amount',
      title: t('glob_billing.amount'),
      render: (row: AccountMovement) => (
        <Text c={getMovementTypeColor(row.amount)} fw={500}>
          {formatCurrency([row.currency, Math.abs(row.amount)])}
        </Text>
      ),
    },
    {
      accessor: 'type',
      title: t('glob_billing.type'),
      render: (row: AccountMovement) => (
        <Badge color={getMovementTypeColor(row.amount)}>
          {getMovementType(row.amount)}
        </Badge>
      ),
    },
    {
      accessor: 'features',
      title: t('glob_billing.features'),
      render: (row: AccountMovement) => parseFeatures(row.features) || '-',
    },
  ];

  return (
    <DataTable
      minHeight={120}
      emptyState={<Box></Box>}
      withTableBorder
      borderRadius="md"
      shadow="xs"
      records={pagination.data || []}
      columns={columns}
      totalRecords={pagination.total}
      recordsPerPage={pagination.perPage}
      page={pagination.page}
      onPageChange={pagination.setPage}
      fetching={pagination.loading}
      loadingText={t('glob_billing.loadingAccountMovements')}
      noRecordsText={t('glob_billing.noAccountMovements')}
    />
  );
}
